/**
 * Address Book API Service
 * 
 * Handles CRUD operations for company contacts (address book).
 * Maps between frontend AddressBookEntry and backend ContactsAddressBook.
 */
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api-client";
import type {
    AddressBookEntry,
    AddressBookRole,
    CreateAddressBookEntryData,
    UpdateAddressBookEntryData,
    AddressBookFilters,
} from "@/types/address-book";

// ============================================================================
// Available Roles Types
// ============================================================================

export interface RoleOption {
    id: string; // Frontend role identifier
    backend_type: string; // Backend relationship_type
    label: {
        en: string;
        it: string;
        es: string;
    };
}

export interface AvailableRolesResponse {
    roles: RoleOption[];
}

// ============================================================================
// Backend Types (matching ContactsAddressBook model)
// ============================================================================

interface BackendContact {
    contact_id: string;
    id?: number;
    company_id: string;
    contact_name: string;
    contact_company_name?: string | null;
    contact_company_id?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: Record<string, unknown> | null;
    relationship_type?: string | null;
    display_role?: string | null;  // Frontend display role (e.g., 'Freight Forwarder', 'Ship Owner')
    tags?: unknown[] | null;
    notes?: string | null;
    is_active: boolean;
    is_favorite: boolean;
    alternative_email?: string | null;
    alternative_phone?: string | null;
    fax?: string | null;
    website?: string | null;
    tax_id?: string | null;
    business_registration_number?: string | null;
    created_at: string;
    updated_at: string;
    created_by_user_id?: string | null;
    updated_by_user_id?: string | null;
    deleted_at?: string | null;
    deleted_by_user_id?: string | null;
    is_verified?: boolean;
    verified_at?: string | null;
    verified_by_user_id?: string | null;
}

interface ContactsListResponse {
    contacts: BackendContact[];
    skip: number;
    limit: number;
    count: number;
}

// ============================================================================
// Role Mapping: Frontend AddressBookRole ↔ Backend RelationshipType
// ============================================================================

// Cache for role mappings from API (populated when getAvailableRoles is called)
let roleMappingCache: Map<string, string> = new Map(); // frontend role -> backend type
let reverseRoleMappingCache: Map<string, string> = new Map(); // backend type -> frontend role

/**
 * Initialize role mapping cache from API data
 * This should be called after getAvailableRoles() is fetched
 */
export function initializeRoleMapping(roles: RoleOption[]): void {
    roleMappingCache.clear();
    reverseRoleMappingCache.clear();
    
    for (const role of roles) {
        roleMappingCache.set(role.id, role.backend_type);
        // For reverse mapping, use the first role that maps to this backend type
        if (!reverseRoleMappingCache.has(role.backend_type)) {
            reverseRoleMappingCache.set(role.backend_type, role.id);
        }
    }
}

/**
 * Map frontend role to backend relationship_type
 * Uses cached mapping from API, with fallback to hardcoded mapping
 */
function mapRoleToRelationshipType(role: AddressBookRole): string {
    // Try cache first (from API)
    if (roleMappingCache.has(role)) {
        return roleMappingCache.get(role)!;
    }
    
    // Fallback to hardcoded mapping (for backwards compatibility or if API not loaded yet)
    const FALLBACK_MAPPING: Record<string, string> = {
        "Shipper": "shipper",
        "Consignee": "receiver",
        "Receiver": "receiver",
        "Notify Party": "notify_party",
        "Freight Forwarder": "freight_forwarder",
        "Forwarder": "freight_forwarder",
        "Carrier": "carrier",
        "Ship Owner": "carrier",
        "Land Transport": "land_transport",
        "Customs Broker": "customs_broker",
        "Port of Loading": "port_authority",
        "Port of Discharge": "port_authority",
        "Airport Staff": "port_authority",
        "Authority": "regulatory_authority",
        "Insurance Provider": "insurance_provider",
        "Inspection Agency": "inspection_agency",
        "Sales": "customer",
        "Supplier": "supplier",
    };
    
    const mappedType = FALLBACK_MAPPING[role] || "customer";
    return mappedType;
}

/**
 * Map backend relationship_type to frontend role
 * Uses cached reverse mapping from API, with fallback
 */
function mapRelationshipTypeToRole(relationshipType: string | null | undefined): AddressBookRole {
    if (!relationshipType) return "Shipper";
    
    // Try cache first (from API)
    if (reverseRoleMappingCache.has(relationshipType)) {
        return reverseRoleMappingCache.get(relationshipType)! as AddressBookRole;
    }
    
    // Fallback mapping
    const FALLBACK_REVERSE_MAPPING: Record<string, AddressBookRole> = {
        shipper: "Shipper",
        receiver: "Receiver",
        notify_party: "Notify Party",
        freight_forwarder: "Freight Forwarder",
        carrier: "Carrier",
        land_transport: "Land Transport",
        customs_broker: "Customs Broker",
        port_authority: "Port of Loading",
        insurance_provider: "Insurance Provider",
        inspection_agency: "Inspection Agency",
        regulatory_authority: "Authority",
        customer: "Sales",
        supplier: "Supplier",
    };
    
    return FALLBACK_REVERSE_MAPPING[relationshipType] || "Shipper";
}

// ============================================================================
// Data Transformation: Frontend ↔ Backend
// ============================================================================

/** Common legal suffixes that indicate a company name, not a person */
const COMPANY_SUFFIXES = [
    'srl', 'sarl', 'sa', 'spa', 'ltd', 'llc', 'gmbh', 'inc', 'corp',
    'corporation', 'bv', 'nv', 'ag', 'plc', 'pty', 'co', 'kg',
    'ohg', 'ek', 'se', 'scrl', 'sas', 'snc', 'scs', 'eurl',
    'oy', 'ab', 'as', 'aps', 'a/s', 'hf', 'ehf', 'sl', 'cb',
];

function isLikelyCompanyName(name: string): boolean {
    if (!name) return false;
    const lower = name.toLowerCase().trim();
    for (const suffix of COMPANY_SUFFIXES) {
        if (lower.endsWith(` ${suffix}`) || lower.endsWith(` ${suffix}.`)) {
            return true;
        }
    }
    if (name === name.toUpperCase() && name.length > 3 && name.includes(' ')) {
        return true;
    }
    return false;
}

/**
 * Convert backend contact to frontend AddressBookEntry
 */
function backendToFrontend(contact: BackendContact, accountId?: string): AddressBookEntry {
    // Split contact_name into firstName and lastName
    // Company names (detected by legal suffixes or ALL CAPS) go entirely into lastName
    const fullName = (contact.contact_name || "").trim();
    let firstName: string;
    let lastName: string;

    if (isLikelyCompanyName(fullName)) {
        firstName = "";
        lastName = fullName;
    } else {
        const nameParts = fullName.split(/\s+/);
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
    }

    // Use display_role if available (preserves exact role selection),
    // otherwise fall back to reverse mapping from relationship_type
    const role = contact.display_role
        ? (contact.display_role as AddressBookRole)
        : mapRelationshipTypeToRole(contact.relationship_type);

    return {
        id: contact.contact_id,
        accountId: accountId || contact.company_id,
        firstName,
        lastName,
        email: contact.email || "",
        phone: contact.phone || undefined,
        notes: contact.notes || undefined,
        companyName: contact.contact_company_name || undefined,
        contactCompanyId: contact.contact_company_id || undefined,
        role,
        isVerified: contact.is_verified || false,
        verifiedAt: contact.verified_at || undefined,
    };
}

/**
 * Convert frontend AddressBookEntry to backend contact data
 */
function frontendToBackend(data: CreateAddressBookEntryData | UpdateAddressBookEntryData): {
    contact_name: string;
    email?: string;
    phone?: string;
    notes?: string;
    contact_company_name?: string;
    contact_company_id?: string;
    relationship_type?: string;
    display_role?: string;
} {
    // If firstName is empty (company contact), just use lastName as full name
    const contactName = data.firstName ? `${data.firstName} ${data.lastName}`.trim() : (data.lastName || "");
    const role = "role" in data ? data.role : undefined;
    const relationshipType = role ? mapRoleToRelationshipType(role) : undefined;

    return {
        contact_name: contactName,
        email: data.email || undefined,
        phone: data.phone || undefined,
        notes: data.notes || undefined,
        contact_company_name: data.companyName || undefined,
        contact_company_id: data.contactCompanyId || undefined,
        relationship_type: relationshipType,
        display_role: role,  // Store the exact frontend role selection
    };
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get available roles for address book contacts
 * This is the single source of truth for available roles
 * Also initializes the role mapping cache
 */
export async function getAvailableRoles(): Promise<RoleOption[]> {
    const response = await apiGet<AvailableRolesResponse>("/contacts/available-roles");
    // Initialize mapping cache
    initializeRoleMapping(response.roles);
    return response.roles;
}

/**
 * Get list of contacts with optional filters
 */
export async function getContacts(
    filters?: AddressBookFilters
): Promise<AddressBookEntry[]> {
    const params: Record<string, string | number | boolean> = {};

    if (filters?.search) {
        params.search = filters.search;
    }
    if (filters?.role) {
        params.relationship_type = mapRoleToRelationshipType(filters.role);
    }
    if (filters?.contactCompanyId) {
        params.contact_company_id = filters.contactCompanyId;
    }
    // accountId is not needed - backend uses current_user.company_id automatically
    // But we'll store it for the response mapping
    const accountId = filters?.accountId;

    const response = await apiGet<ContactsListResponse>("/contacts", {
        params,
    });

    return response.contacts.map((contact) => backendToFrontend(contact, accountId));
}

/**
 * Get a single contact by ID
 */
export async function getContact(contactId: string): Promise<AddressBookEntry> {
    const contact = await apiGet<BackendContact>(`/contacts/${contactId}`);
    return backendToFrontend(contact);
}

/**
 * Create a new contact
 */
export async function createContact(
    data: CreateAddressBookEntryData
): Promise<AddressBookEntry> {
    const backendData = frontendToBackend(data);
    
    const contact = await apiPost<BackendContact>("/contacts", {
        ...backendData,
        is_active: true,
        is_favorite: false,
    });

    return backendToFrontend(contact, data.accountId);
}

/**
 * Update an existing contact
 */
export async function updateContact(
    contactId: string,
    data: UpdateAddressBookEntryData
): Promise<AddressBookEntry> {
    const backendData = frontendToBackend(data);
    
    const contact = await apiPut<BackendContact>(`/contacts/${contactId}`, backendData);

    return backendToFrontend(contact, data.accountId);
}

/**
 * Delete a contact (soft delete)
 */
export async function deleteContact(contactId: string): Promise<void> {
    await apiDelete<void>(`/contacts/${contactId}`);
}

/**
 * Search contacts with filters
 * This is an alias for getContacts with filters
 */
export async function searchContacts(
    filters: AddressBookFilters
): Promise<AddressBookEntry[]> {
    return getContacts(filters);
}

/**
 * Validate a contact (mark as verified)
 */
export async function validateContact(contactId: string): Promise<{ message: string; contact_id: string; is_verified: boolean; verified_at: string | null }> {
    return apiPost(`/contacts/${contactId}/validate`, {});
}

/**
 * Remove validation from a contact
 */
export async function unvalidateContact(contactId: string): Promise<{ message: string; contact_id: string; is_verified: boolean }> {
    return apiPost(`/contacts/${contactId}/unvalidate`, {});
}
